import { displayUnitOf } from "@heraion/shared";
import { useEffect, useMemo, useState } from "react";
import { Pressable, ScrollView, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  coerceLocale,
  type Plan,
  type PlanSeed,
  type PlanStarter,
  type ProgramModel,
} from "@heraion/shared";
import {
  LoggerScreen,
  resolveTheme,
  useBreakpoint,
  useExerciseNameBySlug,
  useMasterDetailSlots,
  type EditingTarget,
  useTodayEmptyState,
} from "@heraion/ui";

import { useProfileDoc } from "./useProfileDoc";
import { useThemePalette } from "./theme";
import { useSessionsList } from "./useSessionsList";
import { useSessionLoader } from "./useSessionLoader";
import { notifyViaWindow } from "./confirm";
import { useLoggerHostDeps } from "./logger/useLoggerHostDeps";
import { useLoggerMaxEditor } from "./maxes/useLoggerMaxEditor";
import { useTodayEmptyHostWeb } from "./useTodayEmptyHostWeb";
import { useTodayRestHours } from "./progress/useTodayRestHours";
import { useMaxBumpFlowWeb } from "./maxes/useMaxBumpFlowWeb";
import type { ComposerRequest } from "./useCreateHubWeb";
import { WorkoutDetailPane } from "./WorkoutDetailPane";
import { useWorkoutDetailData } from "./useWorkoutDetailData";
import { useWebDiary } from "./useWebDiary";
import { WorkoutsListPane } from "./WorkoutsListPane";

// THE Workouts surface on web-rnw — the shared WorkoutsView list plus a
// session detail pane (the shared SessionSummaryView), AND (RW-18) the
// lifted Logger itself: this is the natural mount point per hostDeps.ts —
// the list is already here, and a fresh save is the list refreshing. Master-
// detail is a console concern: wide (>=768) shows the list beside the detail;
// narrow degrades to the phone flow (list, tap pushes to the detail with a
// back affordance). Delete-from-list stays unset (WorkoutsView's own
// documented mobile-only affordance — no destructive gesture on the web
// console). Repeat/Edit (2026-08-18): tap-to-select shows the read-only
// detail pane, and Edit is a button inside it (SessionSummaryView's
// actionSlot); mobile opens the same summary first since #318. Both route
// through useSessionLoader (web twin of the mobile hook), the SAME shared
// hydrate path (EditingTarget/mode on LoggerScreen) mobile's row-tap uses.
// Weights render in kg (web convention). The dual bodymap is the shared
// <BodyMap> (lifted 2026-07-01), painted from the session's volumeByMuscle
// with the theme's grey→peak ramp. useMaxBumpFlowWeb sends every save's MAX
// offer to the server via the LoggerScreen's onSaved(kind, maxBumpInput);
// the proposal lands in the bell (#182).

const LIST_WIDTH = 360;

export function WorkoutsScreen({
  composerRequest,
  onComposerRequestHandled,
  onOpenPainMap,
  onOpenCreateHub,
  onPlanFromModel,
  onPlanSession,
  onOpenPlan,
}: {
  /** RW-18: a `{kind:"workout"}` request from the Create hub auto-opens the
   *  Logger, mirroring PlanScreen's own composerRequest handling for
   *  program/plan requests. */
  composerRequest?: ComposerRequest | null;
  onComposerRequestHandled?: () => void;
  /** AP-9b: the draft's veto line opens the pain map page. */
  onOpenPainMap?: () => void;
  /** The empty «today» draft's «New workout»: the shell's «+» menu. */
  onOpenCreateHub?: () => void;
  /** #359: «Plan a program» after «Save as model» on a session. */
  onPlanFromModel?: (model: Omit<ProgramModel, "id">) => void;
  /** #368: the plan editor on a new plan made from a session. */
  onPlanSession?: (seed: PlanSeed) => void;
  /** #368: a plan of the diary's calendar, in its editor. */
  onOpenPlan?: (plan: Plan) => void;
} = {}) {
  const { dark, family } = useThemePalette();
  const theme = resolveTheme(dark, family);
  const { t, i18n } = useTranslation();
  const locale = coerceLocale(i18n.language);
  const { wide } = useBreakpoint();
  const list = useSessionsList();
  const [collapsedMonths, setCollapsedMonths] = useState<Set<string>>(
    () => new Set(),
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { doc } = useProfileDoc();
  const [loggerOpen, setLoggerOpen] = useState(false);
  // The Plan tab's "Start" (today's session / a program-highlight card)
  // hands its built PlanStarter through the SAME composerRequest channel
  // "New workout" uses — undefined for a plain blank open. Held in its own
  // state (not read back off composerRequest, which the effect below clears
  // immediately) so it survives for the Logger's whole open lifetime.
  const [planStarter, setPlanStarter] = useState<PlanStarter | null>(null);
  const [editing, setEditing] = useState<EditingTarget | null>(null);
  const baseLoggerDeps = useLoggerHostDeps();
  // PG-11 — same shape as mobile: the deps carry the callback, the sheet
  // renders beside the Logger because it is a full-screen Modal of its own.
  const maxEditor = useLoggerMaxEditor();
  const loggerDeps = useMemo(
    () => ({ ...baseLoggerDeps, onEditMax: maxEditor.onEditMax }),
    [baseLoggerDeps, maxEditor.onEditMax],
  );
  const maxBumpFlow = useMaxBumpFlowWeb();
  const todayEmpty = useTodayEmptyState(useTodayEmptyHostWeb);
  const [, setTodayRestHours] = useTodayRestHours();
  // Edit/Repeat's movement-pattern hydration reuses the SAME catalog
  // useMaxBumpFlowWeb already fetches (best-effort, on mount) for its own
  // exercise-name lookup — no second catalog fetch/cache for this screen.
  // ⛔ E lo usa anche il pannello di dettaglio, che prima passava
  // `(slug, fallback) => fallback ?? slug`: senza lo snapshot del nome — le
  // righe vecchie e quelle importate non ce l'hanno — l'atleta leggeva lo
  // SLUG. Il risolutore giusto era gia' su questa superficie, dieci righe
  // sotto (il mount web di PostSessionSummaryCard lo fa da sempre).
  const resolveName = useExerciseNameBySlug(maxBumpFlow.catalog);
  const { openingEditId, repeatingId, onOpenEdit, onRepeat } = useSessionLoader({
    ensureCatalog: async () => maxBumpFlow.catalog ?? [],
    setEditing,
    setLoggerOpen,
    setError: (code) => {
      if (code) notifyViaWindow(t("mobile.workouts.loadError"), t("errors.generic"));
    },
    t,
  });

  // #368: List | Calendar and the selection (a hold, or Select on wide).
  const diary = useWebDiary({
    theme,
    wide,
    items: list.items,
    unit: displayUnitOf(doc),
    catalog: maxBumpFlow.catalog,
    bodyweightKg: (doc?.["bodyweightKg"] as number | undefined) ?? null,
    resolveName,
    onRefresh: list.onRefresh,
    onDeleted: (gone) => setSelectedId((id) => (id !== null && gone.has(id) ? null : id)),
    onStarter: (starter) => {
      setPlanStarter(starter);
      setLoggerOpen(true);
    },
    onPlanSession,
    onOpenPlan,
    onPlanFromModel,
  });

  // Create-hub "New workout" / Plan-tab "Start" → open the Logger, same
  // pattern PlanScreen already uses for program/plan requests (state-based
  // nav, no router).
  useEffect(() => {
    if (composerRequest?.kind !== "workout") return;
    setPlanStarter(composerRequest.planStarter ?? null);
    setLoggerOpen(true);
    onComposerRequestHandled?.();
  }, [composerRequest, onComposerRequestHandled]);
  const { detail, detailNotes, energyReadout, figure, bodyMapValue } = useWorkoutDetailData(
    selectedId,
    list.items,
    doc,
    theme.heatmapRamp,
  );

  const toggleMonth = (key: string) =>
    setCollapsedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const listPane = (
    <WorkoutsListPane
      theme={theme}
      wide={wide}
      diary={diary}
      list={list}
      collapsedMonths={collapsedMonths}
      onToggleMonth={toggleMonth}
      selectedId={selectedId}
      onSelectSession={setSelectedId}
      onNewWorkout={() => {
        setPlanStarter(null);
        setLoggerOpen(true);
      }}
      onRepeat={(id) => void onRepeat(id)}
      repeatingId={repeatingId}
      openingId={openingEditId}
    />
  );

  // Split out to WorkoutDetailPane.tsx (file-size cap, prompt.md §Limiti di
  // dimensione): same four states (no selection / loading / error / loaded
  // summary), same props, no behaviour change.
  // #368 wide: the selection in day order, or the calendar's day.
  const diaryDetail = !wide ? null : diary.selecting ? diary.selectionDetail : diary.mode === "calendar" ? diary.dayList : null;
  const detailBody = diaryDetail ? (
    <ScrollView>{diaryDetail}</ScrollView>
  ) : (
    <WorkoutDetailPane
      theme={theme}
      dark={dark}
      locale={locale}
      unit={displayUnitOf(doc)}
      selectedId={selectedId}
      detail={detail}
      notes={detailNotes}
      resolveName={resolveName}
      onOpenEdit={onOpenEdit}
      openingEditId={openingEditId}
      catalog={maxBumpFlow.catalog}
      onPlanFromModel={onPlanFromModel}
      onPlanSession={onPlanSession}
      bodyMapValue={bodyMapValue}
      figure={figure}
      energyReadout={energyReadout}
      t={t}
    />
  );

  // The Logger itself (RW-18). Opened by the "+ New workout" row above the
  // list or by the Create hub's "New workout" (composerRequest, effect
  // above). `onSaved` refreshes the list AND evaluates the save for a new
  // MAX (same LG-35 rule mobile follows: evaluate on every outcome, not
  // just a server-confirmed one — web never actually answers 'queued', but
  // the shape stays symmetric with mobile's). The Modal stays open to show
  // the post-save summary; the summary's own "Done" CTA closes it (onClose,
  // see useSaveWorkout's postSaveSummary.onDone), same as mobile.
  const loggerNode = (
    <>
      {todayEmpty.probe}
      <LoggerScreen
        visible={loggerOpen}
        editing={editing}
        onClose={() => {
          setLoggerOpen(false);
          setPlanStarter(null);
          setEditing(null);
        }}
        planStarter={planStarter}
        onSaved={(kind, maxBumpInput) => {
          if (kind === "sent") list.onRefresh();
          // #182: the exercises proposed go back for the summary line.
          return maxBumpFlow.evaluateAfterSave(maxBumpInput);
        }}
        onOpenPainMap={onOpenPainMap}
        {...(onOpenCreateHub ? { onOpenCreateHub } : {})}
        {...(todayEmpty.host.onBuildAnyway
          ? { onBuildAnyway: todayEmpty.host.onBuildAnyway }
          : {})}
        weeklyMinimumMet={todayEmpty.host.weeklyMinimumMet}
        onTodayEmptyChange={todayEmpty.onEmptyChange}
        onTodayRestHours={setTodayRestHours}
        deps={loggerDeps}
      />
    </>
  );

  const maxEditorNode = maxEditor.sheet;

  // MX-8's twin, raised stakes: `loggerNode` wraps the Logger MID-WORKOUT
  // draft. The old `if (!wide) return … ; return …` put it and `maxEditorNode`
  // at different array positions between the two branches (narrow had one
  // sibling ahead of them, wide had two), so a resize while logging tore the
  // Logger down and took the draft with it. Now the list/back/detail slots
  // are fixed positions ahead of them (each null when it doesn't apply), so
  // `loggerNode`/`maxEditorNode` sit at the SAME position regardless of
  // `wide` and never remount from a resize. See useMasterDetailSlots for the
  // shared three-slot shape.
  const { showList, showBack, showDetail: showDetailPane } = useMasterDetailSlots(
    wide,
    selectedId !== null,
  );
  const rootStyle = wide
    ? [styles.row, { backgroundColor: theme.palette.bg }]
    : [styles.flex, { backgroundColor: theme.palette.bg }];
  return (
    <View style={rootStyle}>
      {showList ? (
        // MX-8 review: `listPane` wraps `WorkoutsView`, which renders a
        // `SectionList`: state lives in the SUBTREE (scroll offset, cell
        // layout cache), not in `WorkoutsView.tsx` itself, so "no useState in
        // this file" was the wrong test (same miss the review caught here).
        // The wrapper stays ALWAYS present, same as CatalogBrowse's list
        // pane: only its style toggles with `wide`, never its presence.
        <View
          style={
            wide
              ? [styles.listPane, { borderRightColor: theme.palette.border }]
              : styles.flex
          }
        >
          {listPane}
        </View>
      ) : null}
      {showBack ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t("mobile.common.back")}
          onPress={() => setSelectedId(null)}
          style={({ pressed }) => [
            styles.backRow,
            { borderBottomColor: theme.palette.border },
            pressed && { opacity: 0.6 },
          ]}
        >
          <Text style={[styles.backText, { color: theme.palette.brand500 }]}>
            {`← ${t("mobile.common.back")}`}
          </Text>
        </Pressable>
      ) : null}
      {showDetailPane ? (
        <View style={wide ? styles.detailPane : styles.flex}>{detailBody}</View>
      ) : null}
      {loggerNode}
      {maxEditorNode}
      {diary.dialog}
    </View>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  row: { flex: 1, flexDirection: "row" },
  listPane: { width: LIST_WIDTH, borderRightWidth: StyleSheet.hairlineWidth },
  detailPane: { flex: 1, minWidth: 320 },
  backRow: {
    minHeight: 44,
    justifyContent: "center",
    paddingHorizontal: 16,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  backText: { fontSize: 14, fontWeight: "600" },
});
